import React, { useEffect, useState, useMemo } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  MenuItem,
  Select,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import IconButton from '@mui/material/IconButton';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import { DataGrid } from 'react-data-grid';
import { toast } from 'react-toastify';
import PropTypes from 'prop-types';

import ImportExcel from '../../Components/ImportExcel';
import ExportExcel from '../../Components/ExportExcel';
import ConfirmDialog from '../../Components/ConfirmDialog';
import {
  rowKeyGetter,
  autoFocusAndSelect,
  handleCopy,
} from '../../Components/ReactDataGridProps';
import ApiManageReportData from '../../Api/ApiManageReportData';
import { CurrencyFormat } from '../../Utils/Functions';

function ProjectForecast({
  name,
  rows,
  projects,
  selectedForecastType,
  selectedYear,
  selectedCompany,
  selectedOrganisation,
  onClickedAdd,
  onClickedDelete,
  onClickedAllDelete,
  getStateData,
  handleRefetchProjects,
}) {
  const [tableRows, setTableRows] = useState([]);
  const [sortColumns, setSortColumns] = useState([]);
  const [selectedRow, setSelectedRow] = useState(null);
  const [refreshLoading, setRefreshLoading] = useState(false);
  const [simplifyRefreshLoading, setSimplifyRefreshLoading] = useState(false);
  const [showSimplifyRefresh, setShowSimplifyRefresh] = useState(false);
  const [openConfirmDialog, setOpenConfirmDialog] = useState({
    open: false,
    title: '',
    content: '',
    deleteModal: false,
  });

  const sanitizeFilenamePart = (value) => String(value || 'unknown')
    .replace(/[^a-z0-9_-]+/gi, '_')
    .replace(/^_+|_+$/g, '') || 'unknown';

  const downloadRawSynergyExport = (rawSynergyExport) => {
    if (!rawSynergyExport) return;

    const generatedAt = new Date().toISOString();
    const payload = {
      exportType: 'R4D Synergy raw refresh export',
      generatedAt,
      selectedCompany,
      selectedOrganisation,
      selectedYear,
      refreshLabel: 'Project Forecast',
      exports: [rawSynergyExport],
    };

    try {
      const blob = new Blob([JSON.stringify(payload, null, 2)], {
        type: 'application/json;charset=utf-8',
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `synergy-raw-export-project-forecast-${sanitizeFilenamePart(selectedYear)}-${generatedAt.replace(/[:.]/g, '-')}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      toast.error(`Raw Synergy export download failed: ${error.message}`);
    }
  };

  const getComparator = (sortColumn) => {
    switch (sortColumn) {
      case 'Total':
      case 'Jan':
      case 'Feb':
      case 'Mar':
      case 'Apr':
      case 'May':
      case 'Jun':
      case 'Jul':
      case 'Aug':
      case 'Sep':
      case 'Oct':
      case 'Nov':
      case 'Dec':
        return (a, b) => {
          return a[sortColumn] > b[sortColumn];
        };

      case 'ProjectNumber':
        return (a, b) => {
          const aName = getProjectNumberById(a[sortColumn]) || '';
          const bName = getProjectNumberById(b[sortColumn]) || '';
          
          return aName.localeCompare(bName);
        };

      case 'DimProjectKey':
        return (a, b) => {
          const aName = getProjectNameById(a[sortColumn]) || '';
          const bName = getProjectNameById(b[sortColumn]) || '';
          
          return aName.localeCompare(bName);
        };

      default:
        throw new Error(`unsupported sortColumn: "${sortColumn}"`);
    }
  }

  useEffect(() => {
    if (rows) {
      if (rows.length > 1) {
        const sumRow = rows.reduce(multiAdd);
        sumRow.DimChartOfAccountsKey = 'Total';
        sumRow.isFooter = true;
        setTableRows([...rows, sumRow]);
      } else if (rows.length === 1) {
        const sumRow = add(rows[0]);
        sumRow.DimChartOfAccountsKey = 'Total';
        sumRow.isFooter = true;
        setTableRows([...rows, sumRow]);
      } else setTableRows(rows);
    }
  }, [rows]);

  useEffect(() => {
    setSelectedRow(null);
  }, [selectedForecastType]);

  useEffect(() => {
    let active = true;

    ApiManageReportData.getSelectedAccountingApi()
      .then((res) => {
        if (!active) return;
        if (!res || res.status !== 200 || !Array.isArray(res.data)) {
          setShowSimplifyRefresh(false);
          return;
        }

        const simplifyApi = res.data.find((api) => api.ApiType === 'Simplify');
        let accounts = [];
        try {
          accounts = JSON.parse(simplifyApi?.ApiAccounts || '[]');
        } catch (error) {
          accounts = [];
        }

        setShowSimplifyRefresh(
          Array.isArray(accounts) && accounts.includes('Project Forecast'),
        );
      })
      .catch(() => {
        if (active) setShowSimplifyRefresh(false);
      });

    return () => {
      active = false;
    };
  }, [selectedCompany]);

  const getProjectNumberById = (id) => {
    if (!id) return '';
    const project = projects.find(project => project[0] === id);
    return project ? project[11] : '';
  };

  const getProjectNameById = (id) => {
    if (!id) return '';
    const project = projects.find(project => project[0] === id);
    if (!project) return '';
    const pnum = project[11] || '';
    const pname = project[1] || '';
    if (!pname) return pnum;
    const prefix = pnum + ' - ';
    return pname.startsWith(prefix) || pname === pnum ? pname : prefix + pname;
  };

  const add = (a) => {
    const sum = {};
    Object.keys(a).forEach((key) => {
      if (
        key === 'DimCompanyKey' ||
        key === 'DimOrganisationKey' ||
        key === 'id' ||
        key === 'temp_id' ||
        key === 'CreateDate' ||
        key === 'ModDate'
      )
        return (sum[key] = parseInt(a[key]) + parseInt(a[key]));
      return (sum[key] = parseInt(a[key]));
    });
    return sum;
  };

  const multiAdd = (a, b) => {
    const sum = {};
    Object.keys(a).forEach((key) => {
      return (sum[key] = parseInt(a[key]) + parseInt(b[key]));
    });
    return sum;
  };

  const handleCellClick = (rowData) => {
    setSelectedRow(rowData.row);
  };

  const handleRowChange = async (updatedRows, field) => {
    let updated_data = {
      rowIds: [updatedRows[field.indexes[0]].id],
      updated: {
        [field.column.key]: updatedRows[field.indexes[0]][field.column.key],
      },
    };

    if (
      updated_data.updated[field.column.key] === '' ||
      isNaN(updated_data.updated[field.column.key])
    ) {
      toast.warning('Please insert the number value.');
      return;
    }

    if (isNaN(parseFloat(updated_data.updated[field.column.key])) === true)
      updated_data.updated[field.column.key] = 0;
    else
      updated_data.updated[field.column.key] = parseFloat(
        updated_data.updated[field.column.key],
      );

    if (field.column.key !== 'Total') {
      let newTotal =
        parseFloat(updatedRows[field.indexes[0]].Jan) +
        parseFloat(updatedRows[field.indexes[0]].Feb) +
        parseFloat(updatedRows[field.indexes[0]].Mar) +
        parseFloat(updatedRows[field.indexes[0]].Apr) +
        parseFloat(updatedRows[field.indexes[0]].May) +
        parseFloat(updatedRows[field.indexes[0]].Jun) +
        parseFloat(updatedRows[field.indexes[0]].Jul) +
        parseFloat(updatedRows[field.indexes[0]].Aug) +
        parseFloat(updatedRows[field.indexes[0]].Sep) +
        parseFloat(updatedRows[field.indexes[0]].Oct) +
        parseFloat(updatedRows[field.indexes[0]].Nov) +
        parseFloat(updatedRows[field.indexes[0]].Dec);
      updatedRows[field.indexes[0]].Total = newTotal;
      updated_data.updated = { ...updated_data.updated, Total: newTotal };
    }

    let cnt = 0;
    let currentRow = updatedRows[field.indexes[0]];

    updatedRows.forEach((row) => {
      if (
        row.DimCompanyKey === currentRow.DimCompanyKey &&
        row.DimOrganisationKey === currentRow.DimOrganisationKey &&
        row.DimProjectKey === currentRow.DimProjectKey &&
        row.Year === currentRow.Year
      )
        cnt++;
    });

    if (cnt > 1) return;

    let task_res = await ApiManageReportData.updateTemplate(name, updated_data);

    if (task_res.status === 200) {
      ApiManageReportData.getTemplate(name)
        .then((res) => {
          if (res.status === 200) getStateData(res.data);
          else toast.error(res.message);
        })
        .catch((error) => toast.error(error.message));
      setTableRows(updatedRows);
    } else toast.error(task_res.message);
  };

  const handlePaste = ({
    sourceColumnKey,
    sourceRow,
    targetColumnKey,
    targetRow,
  }) => {
    const incompatibleColumns = ['ProjectNumber', 'DimProjectKey'];
    if (
      (incompatibleColumns.includes(targetColumnKey) ||
        incompatibleColumns.includes(sourceColumnKey)) &&
      sourceColumnKey !== targetColumnKey
    ) {
      return targetRow;
    }

    return { ...targetRow, [targetColumnKey]: sourceRow[sourceColumnKey] };
  };

  const onClickSpread = async (ev) => {
    ev.preventDefault();
    if (!selectedRow) return;

    let updated = {};
    let val = parseFloat(selectedRow.Total) / 12;
    updated['Jan'] = val;
    updated['Feb'] = val;
    updated['Mar'] = val;
    updated['Apr'] = val;
    updated['May'] = val;
    updated['Jun'] = val;
    updated['Jul'] = val;
    updated['Aug'] = val;
    updated['Sep'] = val;
    updated['Oct'] = val;
    updated['Nov'] = val;
    updated['Dec'] = val;

    let updated_data = {
      rowIds: [selectedRow.id],
      updated: updated,
    };

    ApiManageReportData.updateTemplate(name, updated_data)
      .then((task_res) => {
        if (task_res.status === 200) {
          ApiManageReportData.getTemplate(name)
            .then((res) => {
              if (res.status === 200) getStateData(res.data);
              else toast.error(res.message);
            })
            .catch((error) => toast.error(error.message));
        } else toast.error(task_res.message);
      })
      .catch((error) => toast.error(error.message));
  };

  const handleClickRefresh = async () => {
    setRefreshLoading(true);

    await ApiManageReportData.refreshTemplate(
      'Project Forecast',
      'Synergy',
      selectedCompany,
      selectedOrganisation,
      selectedYear,
      undefined,
      undefined,
      { includeRawSynergyExport: true },
    )
      .then((res) => {
        if (res.status === 200) {
          getStateData(res.data);
          downloadRawSynergyExport(res.raw_synergy_export);
        } else toast.error(res.message);
      })
      .catch((error) => toast.error(error.message));

    setRefreshLoading(false);
  };

  const handleClickSimplifyRefresh = async () => {
    setSimplifyRefreshLoading(true);

    await ApiManageReportData.refreshTemplate(
      'Project Forecast',
      'Simplify',
      selectedCompany,
      selectedOrganisation,
      selectedYear,
    )
      .then((res) => {
        if (res.status === 200) {
          getStateData(res.data);
          handleRefetchProjects();
        } else toast.error(res.message);
      })
      .catch((error) => toast.error(error.message));

    setSimplifyRefreshLoading(false);
  };

  const onClickedDeleted = () => {
    if (selectedRow) {
      setOpenConfirmDialog({
        open: true,
        title: 'Delete Row',
        content: 'Do you want to really delete the selected row?',
        deleteModal: true,
      });
    }
  };

  const onClickedAllDeleted = () => {
    setOpenConfirmDialog({
      open: true,
      title: 'Delete All Rows',
      content: 'Do you want to really delete all rows?',
      deleteModal: true,
    });
  };

  const handleConfirmDelete = () => {
    if (openConfirmDialog.title === 'Delete Row') {
      onClickedDelete(name, selectedRow.id);
      setSelectedRow(null);
    } else if (openConfirmDialog.title === 'Delete All Rows') {
      onClickedAllDelete(name, selectedForecastType, selectedYear, selectedCompany);
    }

    setTimeout(() => {
      setOpenConfirmDialog({
        open: false,
        title: '',
        content: '',
        deleteModal: false,
      });
    }, 3000);
  };

  const handleExcelImport = async (updated_data) => {
    if (selectedOrganisation === -1) {
      toast.warning('Please select the organisation!');
      return;
    }

    // Helper function to check if required keys exist
    const hasRequiredKeys = (obj) => {
      if (!obj) return false;
      
      const keys = Object.keys(obj);
      
      // Check for different possible key formats with trim() to handle spaces
      const hasTotal = keys.some(key => {
        const trimmedKey = key.trim();
        return trimmedKey.toLowerCase() === 'total' || 
               trimmedKey === 'Total';
      });
      
      const hasMonth1 = keys.some(key => {
        const trimmedKey = key.trim();
        return trimmedKey.toLowerCase() === 'month1' || 
               trimmedKey === 'Month1';
      });
      
      const hasMonth12 = keys.some(key => {
        const trimmedKey = key.trim();
        return trimmedKey.toLowerCase() === 'month12' || 
               trimmedKey === 'Month12';
      });
      
      return hasTotal && hasMonth1 && hasMonth12;
    };

    const escapeRegex = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

    const normalizeDataKeys = (data) => {
      if (!Array.isArray(data)) return data;

      return data.map(row => {
        const normalizedRow = {};
        Object.keys(row).forEach(key => {
          const trimmedKey = key.trim();
          let normalizedKey = trimmedKey;
          
          // Convert month names to expected format
          if (trimmedKey.toLowerCase() === 'month1') normalizedKey = 'Month1';
          else if (trimmedKey.toLowerCase() === 'month2') normalizedKey = 'Month2';
          else if (trimmedKey.toLowerCase() === 'month3') normalizedKey = 'Month3';
          else if (trimmedKey.toLowerCase() === 'month4') normalizedKey = 'Month4';
          else if (trimmedKey.toLowerCase() === 'month5') normalizedKey = 'Month5';
          else if (trimmedKey.toLowerCase() === 'month6') normalizedKey = 'Month6';
          else if (trimmedKey.toLowerCase() === 'month7') normalizedKey = 'Month7';
          else if (trimmedKey.toLowerCase() === 'month8') normalizedKey = 'Month8';
          else if (trimmedKey.toLowerCase() === 'month9') normalizedKey = 'Month9';
          else if (trimmedKey.toLowerCase() === 'month10') normalizedKey = 'Month10';
          else if (trimmedKey.toLowerCase() === 'month11') normalizedKey = 'Month11';
          else if (trimmedKey.toLowerCase() === 'month12') normalizedKey = 'Month12';
          else if (trimmedKey.toLowerCase() === 'total') normalizedKey = 'Total';
          else if (trimmedKey.toLowerCase() === 'project number') normalizedKey = 'ProjectNumber';
          else if (trimmedKey.toLowerCase() === 'project name') normalizedKey = 'ProjectName';
          normalizedRow[normalizedKey] = row[key];
        });
        const pn = normalizedRow.ProjectNumber != null ? String(normalizedRow.ProjectNumber).trim() : '';
        let pname = normalizedRow.ProjectName != null ? String(normalizedRow.ProjectName).trim() : '';
        if (pn && pname) {
          const prefix = new RegExp(`^${escapeRegex(pn)}\\s*-?\\s*`, 'i');
          let prev;
          do {
            prev = pname;
            pname = pname.replace(prefix, '').trim();
          } while (pname !== prev && (pname === pn || pname.startsWith(pn + ' - ') || pname.startsWith(pn + ' -')));
          normalizedRow.ProjectName = pname || prev;
        }
        return normalizedRow;
      });
    };

    try {
      if (hasRequiredKeys(updated_data[0])) {
        const normalized = normalizeDataKeys(updated_data);
        const seen = new Set();
        const normalizedData = normalized.filter((row) => {
          const raw = row.ProjectNumber ?? row['Project Number'];
          const pn = raw != null ? String(raw).trim() : '';
          if (!pn || pn.toLowerCase() === 'total') return false;
          if (seen.has(pn)) return false;
          seen.add(pn);
          return true;
        });
        await onClickedAllDelete(name, selectedForecastType, selectedYear, selectedCompany);
        
        setTimeout(async () => {
          await ApiManageReportData.importDataFromExcelForBFC(
            name,
            normalizedData,
            selectedYear,
            selectedForecastType,
            selectedOrganisation,
          )
          .then((res) => {
            if (res.status === 200) {
              if (res.new_project_flag) {
                handleRefetchProjects();
              }
              getStateData(res.data);
            }
            else toast.error(res.message);
          })
          .catch((error) => toast.error(error.message));
        }, 1000);
      } else {
        console.log('Missing required keys. Expected: Total, Month1/Jan, Month12/Dec');
        console.log('Found keys:', Object.keys(updated_data[0] || {}));
        toast.warning('Excel file format is incorrect. Please check the column headers. Expected: Total, Month1 (or Jan), Month12 (or Dec)');
      }
    } catch (error) {
      toast.warning(
        'Please check the excel file! This file is not correct!',
      );
    }
  };

  const tableColumns = useMemo(() => {
    return [
      {
        key: 'ProjectNumber',
        name: 'Project Number',
        resizable: true,
        sortable: true,
        width: 300,
        renderCell: (props) => {
          if (props.row.isFooter)
            return (
              <div
                style={{
                  background: '#e0e0e0',
                  height: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                Total
              </div>
            );
          else {
            let sortProjects = projects.sort((a, b) => {
              const aVal = a[11] || '';
              const bVal = b[11] || '';
              return aVal.localeCompare(bVal);
            });
            return (
              <FormControl variant="standard">
                <Select
                  sx={{ width: 300, height: 50 }}
                  value={props.row.DimProjectKey ? props.row.DimProjectKey : ''}
                  onChange={(e) =>
                    props.onRowChange(
                      { ...props.row, DimProjectKey: e.target.value },
                      true,
                    )
                  }
                >
                  {sortProjects &&
                    sortProjects.map((cur) => (
                      <MenuItem
                        key={cur[0]}
                        value={cur[0]}
                        style={{ display: cur[2] ? 'block' : 'none' }}
                        disabled={
                          tableRows.find((row) => row.DimProjectKey === cur[0])
                            ? true
                            : false
                        }
                      >
                        {cur[11]}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            );
          }
        },
      },
      {
        key: 'DimProjectKey',
        name: 'Project Name',
        resizable: true,
        sortable: true,
        width: 300,
        renderCell: (props) => {
          if (props.row.isFooter)
            return (
              <div
                style={{
                  background: '#e0e0e0',
                  height: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              ></div>
            );
          else {
            let sortProjects = projects.sort((a, b) => {
              const aVal = a[1] || '';
              const bVal = b[1] || '';
              return aVal.localeCompare(bVal);
            });

            return (
              <FormControl variant="standard">
                <Select
                  sx={{ width: 300 }}
                  className="projectName"
                  value={props.row.DimProjectKey ? props.row.DimProjectKey : ''}
                  onChange={(e) =>
                    props.onRowChange(
                      { ...props.row, DimProjectKey: e.target.value },
                      true,
                    )
                  }
                >
                  {sortProjects &&
                    sortProjects.map((cur) => (
                      <MenuItem
                        key={cur[0]}
                        value={cur[0]}
                        style={{ display: cur[2] ? 'block' : 'none' }}
                        disabled={
                          tableRows.find((row) => row.DimProjectKey === cur[0])
                            ? true
                            : false
                        }
                      >
                        {cur[11] + ' - ' + cur[1]}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            );
          }
        },
      },
      {
        key: 'Total',
        name: 'Total',
        resizable: true,
        sortable: true,
        width: 175,        
        renderEditCell: (props) =>
          props.row.isFooter ? (
            <div
              style={{
                background: '#e0e0e0',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {CurrencyFormat(props.row.Total)}
            </div>
          ) : (
            <input
              className="rdg-text-editor"
              ref={autoFocusAndSelect}
              value={props.row.Total}
              type="number"
              onChange={(event) =>
                props.onRowChange({
                  ...props.row,
                  Total: event.target.value,
                })
              }
              onBlur={() => props.onClose(true, false)}
            />
          ),
        renderCell: (props) => (
          <div
            style={{
              background: '#e0e0e0',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {CurrencyFormat(props.row.Total)}
          </div>
        ),
      },
      {
        key: 'Jan',
        name: '1',
        sortable: true,
        resizable: true,
        minWidth: 100,
        renderEditCell: (props) =>
          props.row.isFooter ? (
            <div
              style={{
                background: '#e0e0e0',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {CurrencyFormat(props.row.Jan)}
            </div>
          ) : (
            <input
              className="rdg-text-editor"
              ref={autoFocusAndSelect}
              value={props.row.Jan}
              type="number"
              onChange={(event) =>
                props.onRowChange({
                  ...props.row,
                  Jan: event.target.value,
                })
              }
              onBlur={() => props.onClose(true, false)}
            />
          ),
        renderCell: (props) => {
          if (props.row.isFooter)
            return (
              <div
                style={{
                  background: '#e0e0e0',
                  height: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {CurrencyFormat(props.row.Jan)}
              </div>
            );
          else return <div>{CurrencyFormat(props.row.Jan)}</div>;
        },
      },
      {
        key: 'Feb',
        name: '2',
        sortable: true,
        resizable: true,
        minWidth: 100,
        renderEditCell: (props) =>
          props.row.isFooter ? (
            <div
              style={{
                background: '#e0e0e0',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {CurrencyFormat(props.row.Feb)}
            </div>
          ) : (
            <input
              className="rdg-text-editor"
              ref={autoFocusAndSelect}
              value={props.row.Feb}
              type="number"
              onChange={(event) =>
                props.onRowChange({
                  ...props.row,
                  Feb: event.target.value,
                })
              }
              onBlur={() => props.onClose(true, false)}
            />
          ),
        renderCell: (props) => {
          if (props.row.isFooter)
            return (
              <div
                style={{
                  background: '#e0e0e0',
                  height: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {CurrencyFormat(props.row.Feb)}
              </div>
            );
          else return <div>{CurrencyFormat(props.row.Feb)}</div>;
        },
      },
      {
        key: 'Mar',
        name: '3',
        sortable: true,
        resizable: true,
        minWidth: 100,
        renderEditCell: (props) =>
          props.row.isFooter ? (
            <div
              style={{
                background: '#e0e0e0',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {CurrencyFormat(props.row.Mar)}
            </div>
          ) : (
            <input
              className="rdg-text-editor"
              ref={autoFocusAndSelect}
              value={props.row.Mar}
              type="number"
              onChange={(event) =>
                props.onRowChange({
                  ...props.row,
                  Mar: event.target.value,
                })
              }
              onBlur={() => props.onClose(true, false)}
            />
          ),
        renderCell: (props) => {
          if (props.row.isFooter)
            return (
              <div
                style={{
                  background: '#e0e0e0',
                  height: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {CurrencyFormat(props.row.Mar)}
              </div>
            );
          else return <div>{CurrencyFormat(props.row.Mar)}</div>;
        },
      },
      {
        key: 'Apr',
        name: '4',
        sortable: true,
        resizable: true,
        minWidth: 100,
        renderEditCell: (props) =>
          props.row.isFooter ? (
            <div
              style={{
                background: '#e0e0e0',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {CurrencyFormat(props.row.Apr)}
            </div>
          ) : (
            <input
              className="rdg-text-editor"
              ref={autoFocusAndSelect}
              value={props.row.Apr}
              type="number"
              onChange={(event) =>
                props.onRowChange({
                  ...props.row,
                  Apr: event.target.value,
                })
              }
              onBlur={() => props.onClose(true, false)}
            />
          ),
        renderCell: (props) => {
          if (props.row.isFooter)
            return (
              <div
                style={{
                  background: '#e0e0e0',
                  height: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {CurrencyFormat(props.row.Apr)}
              </div>
            );
          else return <div>{CurrencyFormat(props.row.Apr)}</div>;
        },
      },
      {
        key: 'May',
        name: '5',
        sortable: true,
        resizable: true,
        minWidth: 100,
        renderEditCell: (props) =>
          props.row.isFooter ? (
            <div
              style={{
                background: '#e0e0e0',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {CurrencyFormat(props.row.May)}
            </div>
          ) : (
            <input
              className="rdg-text-editor"
              ref={autoFocusAndSelect}
              value={props.row.May}
              type="number"
              onChange={(event) =>
                props.onRowChange({
                  ...props.row,
                  May: event.target.value,
                })
              }
              onBlur={() => props.onClose(true, false)}
            />
          ),
        renderCell: (props) => {
          if (props.row.isFooter)
            return (
              <div
                style={{
                  background: '#e0e0e0',
                  height: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {CurrencyFormat(props.row.May)}
              </div>
            );
          else return <div>{CurrencyFormat(props.row.May)}</div>;
        },
      },
      {
        key: 'Jun',
        name: '6',
        sortable: true,
        resizable: true,
        minWidth: 100,
        renderEditCell: (props) =>
          props.row.isFooter ? (
            <div
              style={{
                background: '#e0e0e0',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {CurrencyFormat(props.row.Jun)}
            </div>
          ) : (
            <input
              className="rdg-text-editor"
              ref={autoFocusAndSelect}
              value={props.row.Jun}
              type="number"
              onChange={(event) =>
                props.onRowChange({
                  ...props.row,
                  Jun: event.target.value,
                })
              }
              onBlur={() => props.onClose(true, false)}
            />
          ),
        renderCell: (props) => {
          if (props.row.isFooter)
            return (
              <div
                style={{
                  background: '#e0e0e0',
                  height: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {CurrencyFormat(props.row.Jun)}
              </div>
            );
          else return <div>{CurrencyFormat(props.row.Jun)}</div>;
        },
      },
      {
        key: 'Jul',
        name: '7',
        sortable: true,
        resizable: true,
        minWidth: 100,
        renderEditCell: (props) =>
          props.row.isFooter ? (
            <div
              style={{
                background: '#e0e0e0',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {CurrencyFormat(props.row.Jul)}
            </div>
          ) : (
            <input
              className="rdg-text-editor"
              ref={autoFocusAndSelect}
              value={props.row.Jul}
              type="number"
              onChange={(event) =>
                props.onRowChange({
                  ...props.row,
                  Jul: event.target.value,
                })
              }
              onBlur={() => props.onClose(true, false)}
            />
          ),
        renderCell: (props) => {
          if (props.row.isFooter)
            return (
              <div
                style={{
                  background: '#e0e0e0',
                  height: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {CurrencyFormat(props.row.Jul)}
              </div>
            );
          else return <div>{CurrencyFormat(props.row.Jul)}</div>;
        },
      },
      {
        key: 'Aug',
        name: '8',
        sortable: true,
        resizable: true,
        minWidth: 100,
        renderEditCell: (props) =>
          props.row.isFooter ? (
            <div
              style={{
                background: '#e0e0e0',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {CurrencyFormat(props.row.Aug)}
            </div>
          ) : (
            <input
              className="rdg-text-editor"
              ref={autoFocusAndSelect}
              value={props.row.Aug}
              type="number"
              onChange={(event) =>
                props.onRowChange({
                  ...props.row,
                  Aug: event.target.value,
                })
              }
              onBlur={() => props.onClose(true, false)}
            />
          ),
        renderCell: (props) => {
          if (props.row.isFooter)
            return (
              <div
                style={{
                  background: '#e0e0e0',
                  height: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {CurrencyFormat(props.row.Aug)}
              </div>
            );
          else return <div>{CurrencyFormat(props.row.Aug)}</div>;
        },
      },
      {
        key: 'Sep',
        name: '9',
        sortable: true,
        resizable: true,
        minWidth: 100,
        renderEditCell: (props) =>
          props.row.isFooter ? (
            <div
              style={{
                background: '#e0e0e0',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {CurrencyFormat(props.row.Sep)}
            </div>
          ) : (
            <input
              className="rdg-text-editor"
              ref={autoFocusAndSelect}
              value={props.row.Sep}
              type="number"
              onChange={(event) =>
                props.onRowChange({
                  ...props.row,
                  Sep: event.target.value,
                })
              }
              onBlur={() => props.onClose(true, false)}
            />
          ),
        renderCell: (props) => {
          if (props.row.isFooter)
            return (
              <div
                style={{
                  background: '#e0e0e0',
                  height: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {CurrencyFormat(props.row.Sep)}
              </div>
            );
          else return <div>{CurrencyFormat(props.row.Sep)}</div>;
        },
      },
      {
        key: 'Oct',
        name: '10',
        sortable: true,
        resizable: true,
        minWidth: 100,
        renderEditCell: (props) =>
          props.row.isFooter ? (
            <div
              style={{
                background: '#e0e0e0',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {CurrencyFormat(props.row.Oct)}
            </div>
          ) : (
            <input
              className="rdg-text-editor"
              ref={autoFocusAndSelect}
              value={props.row.Oct}
              type="number"
              onChange={(event) =>
                props.onRowChange({
                  ...props.row,
                  Oct: event.target.value,
                })
              }
              onBlur={() => props.onClose(true, false)}
            />
          ),
        renderCell: (props) => {
          if (props.row.isFooter)
            return (
              <div
                style={{
                  background: '#e0e0e0',
                  height: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {CurrencyFormat(props.row.Oct)}
              </div>
            );
          else return <div>{CurrencyFormat(props.row.Oct)}</div>;
        },
      },
      {
        key: 'Nov',
        name: '11',
        sortable: true,
        resizable: true,
        minWidth: 100,
        renderEditCell: (props) =>
          props.row.isFooter ? (
            <div
              style={{
                background: '#e0e0e0',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {CurrencyFormat(props.row.Nov)}
            </div>
          ) : (
            <input
              className="rdg-text-editor"
              ref={autoFocusAndSelect}
              value={props.row.Nov}
              type="number"
              onChange={(event) =>
                props.onRowChange({
                  ...props.row,
                  Nov: event.target.value,
                })
              }
              onBlur={() => props.onClose(true, false)}
            />
          ),
        renderCell: (props) => {
          if (props.row.isFooter)
            return (
              <div
                style={{
                  background: '#e0e0e0',
                  height: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {CurrencyFormat(props.row.Nov)}
              </div>
            );
          else return <div>{CurrencyFormat(props.row.Nov)}</div>;
        },
      },
      {
        key: 'Dec',
        name: '12',
        sortable: true,
        resizable: true,
        minWidth: 100,
        renderEditCell: (props) =>
          props.row.isFooter ? (
            <div
              style={{
                background: '#e0e0e0',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {CurrencyFormat(props.row.Dec)}
            </div>
          ) : (
            <input
              className="rdg-text-editor"
              ref={autoFocusAndSelect}
              value={props.row.Dec}
              type="number"
              onChange={(event) =>
                props.onRowChange({
                  ...props.row,
                  Dec: event.target.value,
                })
              }
              onBlur={() => props.onClose(true, false)}
            />
          ),
        renderCell: (props) => {
          if (props.row.isFooter)
            return (
              <div
                style={{
                  background: '#e0e0e0',
                  height: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {CurrencyFormat(props.row.Dec)}
              </div>
            );
          else return <div>{CurrencyFormat(props.row.Dec)}</div>;
        },
      },
    ];
  }, [tableRows, projects]);

  const sortedRows = useMemo(() => {
    if (sortColumns.length === 0) return tableRows;

    return [...tableRows].sort((a, b) => {
      for (const sort of sortColumns) {
        const comparator = getComparator(sort.columnKey);
        const compResult = comparator(a, b);
        if (compResult !== 0) {
          return sort.direction === 'ASC' ? compResult : -compResult;
        }
      }
      return 0;
    });
  }, [tableRows, sortColumns]);

  const exportValue = useMemo(() => {
    let export_value = {
      [selectedForecastType]: {
        columns: [],
        data: [],
      },
      Projects: {
        columns: [],
        data: [],
      },
    };

    export_value[selectedForecastType]['columns'] = [
      { title: 'ProjectNumber', width: { wpx: 80 } },
      { title: 'ProjectName', width: { wpx: 80 } },
      { title: 'Total', width: { wpx: 80 } },
      { title: 'Month1', width: { wpx: 80 } },
      { title: 'Month2', width: { wpx: 80 } },
      { title: 'Month3', width: { wpx: 80 } },
      { title: 'Month4', width: { wpx: 80 } },
      { title: 'Month5', width: { wpx: 80 } },
      { title: 'Month6', width: { wpx: 80 } },
      { title: 'Month7', width: { wpx: 80 } },
      { title: 'Month8', width: { wpx: 80 } },
      { title: 'Month9', width: { wpx: 80 } },
      { title: 'Month10', width: { wpx: 80 } },
      { title: 'Month11', width: { wpx: 80 } },
      { title: 'Month12', width: { wpx: 80 } },
    ];

    sortedRows.forEach((data) => {
      if (data.DimProjectKey === 'Total') {
        export_value[selectedForecastType]['data'].push([
          { value: 'Total' },
          {
            value: projects.find((pro) => pro.id === data.DimProjectKey)
              ? projects.find((pro) => pro.id === data.DimProjectKey)
                  .ProjectName
              : '',
          },
          {
            value: data.Total ? Number(data.Total) : 0,
            style: { numFmt: '0.00' },
          },
          { value: data.Jan ? Number(data.Jan) : 0, style: { numFmt: '0.00' } },
          { value: data.Feb ? Number(data.Feb) : 0, style: { numFmt: '0.00' } },
          { value: data.Mar ? Number(data.Mar) : 0, style: { numFmt: '0.00' } },
          { value: data.Apr ? Number(data.Apr) : 0, style: { numFmt: '0.00' } },
          { value: data.May ? Number(data.May) : 0, style: { numFmt: '0.00' } },
          { value: data.Jun ? Number(data.Jun) : 0, style: { numFmt: '0.00' } },
          { value: data.Jul ? Number(data.Jul) : 0, style: { numFmt: '0.00' } },
          { value: data.Aug ? Number(data.Aug) : 0, style: { numFmt: '0.00' } },
          { value: data.Sep ? Number(data.Sep) : 0, style: { numFmt: '0.00' } },
          { value: data.Oct ? Number(data.Oct) : 0, style: { numFmt: '0.00' } },
          { value: data.Nov ? Number(data.Nov) : 0, style: { numFmt: '0.00' } },
          { value: data.Dec ? Number(data.Dec) : 0, style: { numFmt: '0.00' } },
        ]);
      } else {
        export_value[selectedForecastType]['data'].push([
          {
            value: projects.find((pro) => pro[0] === data.DimProjectKey)
              ? projects.find((pro) => pro[0] === data.DimProjectKey)[11]
              : '',
          },
          {
            value: projects.find((pro) => pro[0] === data.DimProjectKey)
              ? projects.find((pro) => pro[0] === data.DimProjectKey)[1]
              : '',
          },
          {
            value: data.Total ? Number(data.Total) : 0,
            style: { numFmt: '0.00' },
          },
          { value: data.Jan ? Number(data.Jan) : 0, style: { numFmt: '0.00' } },
          { value: data.Feb ? Number(data.Feb) : 0, style: { numFmt: '0.00' } },
          { value: data.Mar ? Number(data.Mar) : 0, style: { numFmt: '0.00' } },
          { value: data.Apr ? Number(data.Apr) : 0, style: { numFmt: '0.00' } },
          { value: data.May ? Number(data.May) : 0, style: { numFmt: '0.00' } },
          { value: data.Jun ? Number(data.Jun) : 0, style: { numFmt: '0.00' } },
          { value: data.Jul ? Number(data.Jul) : 0, style: { numFmt: '0.00' } },
          { value: data.Aug ? Number(data.Aug) : 0, style: { numFmt: '0.00' } },
          { value: data.Sep ? Number(data.Sep) : 0, style: { numFmt: '0.00' } },
          { value: data.Oct ? Number(data.Oct) : 0, style: { numFmt: '0.00' } },
          { value: data.Nov ? Number(data.Nov) : 0, style: { numFmt: '0.00' } },
          { value: data.Dec ? Number(data.Dec) : 0, style: { numFmt: '0.00' } },
        ]);
      }
    });

    export_value['Projects']['columns'] = [
      { title: 'id', width: { wpx: 80 } },
      { title: 'AccountNumber', width: { wpx: 80 } },
      { title: 'Name', width: { wpx: 80 } },
    ];

    export_value['Projects']['data'] =
      projects &&
      projects.map((pro) => {
        return [
          { value: Number(pro[0]), style: { numFmt: '0' } },
          { value: pro[11] },
          { value: pro[1] },
        ];
      });

    return export_value;
  }, [selectedForecastType, sortedRows, projects]);
  
  return (
    <div className="formClass">
      <div style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
        <div style={{ flex: 1 }} />
        <ImportExcel onImport={handleExcelImport} tabIdx={3} />
        <ExportExcel filename={selectedForecastType} value={exportValue} />
        <Button
          onClick={onClickSpread}
          variant="contained"
          color="primary"
          size="small"
          style={{ marginLeft: 8, marginRight: 8 }}
        >
          Spread
        </Button>
        <Box sx={{ display: 'inline', position: 'relative' }}>
          <Button
            variant="contained"
            color="secondary"
            size="small"
            disabled={refreshLoading}
            onClick={handleClickRefresh}
          >
            Refresh
          </Button>
          {refreshLoading && (
            <CircularProgress
              size={24}
              sx={{
                color: '#4caf50',
                position: 'absolute',
                top: '60%',
                left: '50%',
                marginTop: '-12px',
                marginLeft: '-12px',
              }}
            />
          )}
        </Box>
        {showSimplifyRefresh && (
          <Box sx={{ display: 'inline', position: 'relative', marginLeft: '8px' }}>
            <Button
              variant="contained"
              color="secondary"
              size="small"
              disabled={simplifyRefreshLoading}
              onClick={handleClickSimplifyRefresh}
            >
              Simplfy Refresh
            </Button>
            {simplifyRefreshLoading && (
              <CircularProgress
                size={24}
                sx={{
                  color: '#4caf50',
                  position: 'absolute',
                  top: '60%',
                  left: '50%',
                  marginTop: '-12px',
                  marginLeft: '-12px',
                }}
              />
            )}
          </Box>
        )}
        <IconButton
          aria-label="Add"
          onClick={() => {
            onClickedAdd(name, selectedRow ? selectedRow.temp_id : 0);
          }}
        >
          <AddIcon />
        </IconButton>
        <IconButton aria-label="Delete" onClick={onClickedDeleted}>
          <DeleteIcon />
        </IconButton>
        <IconButton aria-label="deleteAll" onClick={onClickedAllDeleted}>
          <DeleteSweepIcon />
        </IconButton>
      </div>

      <DataGrid
        rowKeyGetter={rowKeyGetter}
        className="fill-grid rdg-light"
        columns={tableColumns}
        rows={sortedRows}
        enableCellSelect={true}
        sortColumns={sortColumns}
        onSortColumnsChange={setSortColumns}
        onRowsChange={handleRowChange}
        onCellClick={handleCellClick}
        onCopy={handleCopy}
        onPaste={handlePaste}
        rowHeight={50}
        style={{ maxHeight: window.innerHeight - 233 }}
      />

      <ConfirmDialog
        open={openConfirmDialog.open}
        title={openConfirmDialog.title}
        content={openConfirmDialog.content}
        deleteModal={openConfirmDialog.deleteModal}
        onClose={() => setOpenConfirmDialog({ ...openConfirmDialog, open: false })}
        onConfirm={handleConfirmDelete}
      />
    </div>
  );
}

ProjectForecast.propTypes = {
  name: PropTypes.string.isRequired,
  rows: PropTypes.array.isRequired,
  projects: PropTypes.array.isRequired,
  selectedForecastType: PropTypes.string.isRequired,
  selectedYear: PropTypes.number.isRequired,
  selectedCompany: PropTypes.number.isRequired,
  selectedOrganisation: PropTypes.number.isRequired,
  onClickedAdd: PropTypes.func.isRequired,
  onClickedDelete: PropTypes.func.isRequired,
  onClickedAllDelete: PropTypes.func.isRequired,
  getStateData: PropTypes.func.isRequired,
  handleRefetchProjects: PropTypes.func.isRequired,
};

export default ProjectForecast;
